import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const ServiceModal = ({ service, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!service?.id;
  const [form, setForm] = useState({
    name:        service?.name        || "",
    description: service?.description || "",
    icon:        service?.icon        || "fa-solid fa-code",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { show("Name is required", "error"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.services}/${service.id}`, {
          method: "PATCH",
          body: JSON.stringify(form),
        });
      } else {
        await authFetch(ENDPOINTS.services, {
          method: "POST",
          body: JSON.stringify(form),
        });
      }
      show(isEdit ? "Service updated!" : "Service created!");
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const ICON_OPTIONS = [
    "fa-solid fa-code",
    "fa-solid fa-mobile-screen",
    "fa-solid fa-brain",
    "fa-solid fa-cloud",
    "fa-solid fa-pen-ruler",
    "fa-solid fa-lightbulb",
    "fa-solid fa-server",
    "fa-solid fa-shield-halved",
    "fa-solid fa-chart-bar",
    "fa-solid fa-rocket",
  ];

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Service" : "Add Service"}</h3>
            <button className="adm-modal-close" onClick={onClose}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-field">
                  <label className="adm-form-label">Service Name *</label>
                  <input name="name" className="adm-form-input" value={form.name}
                    onChange={handleChange} placeholder="e.g. Web Development" required />
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Description</label>
                  <textarea name="description" className="adm-form-textarea"
                    value={form.description} onChange={handleChange}
                    placeholder="Short description of this service" />
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Icon (FontAwesome class)</label>
                  <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <input name="icon" className="adm-form-input" value={form.icon}
                      onChange={handleChange} placeholder="fa-solid fa-code" style={{ flex: 1 }} />
                    <div style={{
                      width: 42, height: 42, background: "rgba(10,107,90,0.15)",
                      border: "1px solid rgba(10,107,90,0.3)", borderRadius: 8,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#0a9b7c", fontSize: "1.1rem", flexShrink: 0,
                    }}>
                      <i className={form.icon}></i>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                    {ICON_OPTIONS.map((ic) => (
                      <button key={ic} type="button"
                        onClick={() => setForm((p) => ({ ...p, icon: ic }))}
                        style={{
                          width: 34, height: 34, borderRadius: 6, cursor: "pointer",
                          background: form.icon === ic ? "rgba(10,107,90,0.25)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${form.icon === ic ? "rgba(10,107,90,0.5)" : "rgba(255,255,255,0.08)"}`,
                          color: form.icon === ic ? "#0a9b7c" : "rgba(255,255,255,0.4)",
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem",
                        }}>
                        <i className={ic}></i>
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? "Saving..." : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Create"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

const DeleteConfirm = ({ item, label, onClose, onDeleted, endpoint }) => {
  const [deleting, setDeleting] = useState(false);
  const confirm = async () => {
    setDeleting(true);
    try {
      await authFetch(`${endpoint}/${item.id}`, { method: "DELETE" });
      onDeleted();
      onClose();
    } catch (err) {
      alert(err.message || "Delete failed");
      setDeleting(false);
    }
  };
  return (
    <div className="adm-modal-backdrop" onClick={onClose}>
      <div className="adm-modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3 className="adm-modal-title">Delete {label}</h3>
          <button className="adm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <div className="adm-modal-body">
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem", lineHeight: 1.6 }}>
            Are you sure you want to delete <strong style={{ color: "white" }}>{item.name || item.title || item.client_name}</strong>? This cannot be undone.
          </p>
        </div>
        <div className="adm-modal-footer">
          <button className="adm-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="adm-save-btn" style={{ background: "linear-gradient(135deg,#b91c1c,#7f1d1d)" }}
            onClick={confirm} disabled={deleting}>
            {deleting ? "Deleting..." : <><i className="fa-solid fa-trash"></i> Delete</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export DeleteConfirm for reuse in other modules
export { DeleteConfirm };

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const { toast, show }         = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.services);
      setServices(data);
    } catch (err) {
      show(err.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Services</h1>
          <p className="adm-page-sub">{services.length} services</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-plus"></i> Add Service
        </button>
      </div>

      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading"><div className="adm-loading-spinner"></div></div>
        ) : services.length === 0 ? (
          <div className="adm-empty">No services yet.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr><th>Icon</th><th>Name</th><th>Description</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: "rgba(10,107,90,0.15)", border: "1px solid rgba(10,107,90,0.25)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#0a9b7c", fontSize: "0.9rem",
                    }}>
                      <i className={s.icon || "fa-solid fa-circle"}></i>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{s.name}</td>
                  <td style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.825rem", maxWidth: 300 }}>
                    {s.description?.substring(0, 80)}{s.description?.length > 80 ? "..." : ""}
                  </td>
                  <td>
                    <div className="adm-action-btns">
                      <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", item: s })}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className="adm-delete-btn" onClick={() => setModal({ type: "delete", item: s })}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal?.type === "add" && (
        <ServiceModal service={null} onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal?.type === "edit" && (
        <ServiceModal service={modal.item} onClose={() => setModal(null)} onSaved={load} />
      )}
      {modal?.type === "delete" && (
        <DeleteConfirm item={modal.item} label="Service" endpoint={ENDPOINTS.services}
          onClose={() => setModal(null)} onDeleted={() => { load(); show("Service deleted."); }} />
      )}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminServices;
